// Student 360 in the CRM: the Directory tab, the student page, the dashboard student strip and the
// teacher portal header. Enumerations the API sends as upper-case codes (ACTIVE, PHONE_OTP, ...)
// are translated here by code. Times are written "3 h ago" / "5 d ago" with the unit abbreviated so
// no plural forms are needed in any language; counts use "Label: {count}" for the same reason.
const student360 = {
  en: {
    tabDirectory: 'Directory',
    tabByCourse: 'By course',
    tabAiRisk: 'AI risk score',
    atRisk: 'At risk',
    atRiskHint: 'Enrolled in a course and not seen for 7 days or more',
    hub: {
      title: 'Students',
      subtitle: 'Track enrollment progress and spot students who need attention.',
      tabsLabel: 'Student views',
    },
    filters: { title: 'Filters', done: 'Done' },
    roster: {
      selectCourse: 'Select a course...',
      searchPlaceholder: 'Search name, email or phone',
      subtitle: 'Enrollment roster & progress by course',
      rosterCount: '{count} students enrolled',
      pickTitle: 'Pick a course to get started',
      pickDesc: 'Choose one of your courses above to see enrolled students, their progress and status.',
      loading: 'Loading students...',
      emptyTitle: 'No students enrolled yet',
      emptyDesc: 'Once students enroll in this course, they will appear here.',
      avgLine: 'Avg. progress {percent}%',
      searchDisabled: 'Pick a course first',
      filterAll: 'All',
      filterInProgress: 'In progress',
      filterCompleted: 'Completed',
      filterNotStarted: 'Not started',
      filterInactive: 'No access',
      statusCompleted: 'Completed',
      statusInProgress: 'In progress',
      statusNotStarted: 'Not started',
      statusInactive: 'No access',
      colStudent: 'Student',
      colProgress: 'Progress',
      colStatus: 'Status',
      colGroups: 'Groups',
      colEnrolled: 'Enrolled',
      colCompleted: 'Completed',
      noMatch: 'No students match this filter.',
      showing: 'Showing {shown} of {total} students',
      showingAll: 'Showing {shown} students',
      rosterCapped: 'Only the first {loaded} of {total} students are loaded; the counts and search cover those.',
      allGroups: 'All groups',
      addToGroup: 'Add to a group',
      assignHead: 'Add to a group',
      inAllGroups: 'Already in all groups',
      noGroupsYet: 'No groups yet. Create one on the Teams page.',
      addedToGroup: 'Added to {group}',
      addGroupFailed: 'Could not add to group',
      viewDetail: 'View student details',
    },
    notEnrolled: 'Not enrolled',
    moreCourses: '+{count} more',
    joinedOn: 'Joined {date}',
    ago: {
      justNow: 'just now',
      minutes: '{n} min ago',
      hours: '{n} h ago',
      days: '{n} d ago',
      today: 'today',
      yesterday: 'yesterday',
    },
    presence: {
      online: 'Online now',
      onlineHint: 'Active within the last 5 minutes',
      never: 'Never seen',
      neverHint: 'No visit recorded yet',
      approximateHint: 'Approximate: taken from the last study activity. Exact tracking starts with the next visit.',
      tashkentTime: 'Tashkent time',
      loginUnknownHint: 'Not recorded yet: it fills in at the next sign-in',
      lastSeen: 'Last seen',
      lastLogin: 'Last login',
      loginAgo: 'Login: {when}',
    },
    lesson: {
      current: 'Current lesson',
      short: '{index} / {total}',
      position: 'Lesson {index} of {total}',
      pausedAt: 'paused at {time}',
      reason: {
        inProgress: 'In progress',
        next: 'Up next',
        first: 'Not started',
        finished: 'Course finished',
      },
    },
    enrollment: { ACTIVE: 'Active', COMPLETED: 'Completed', INACTIVE: 'No access' },
    account: { BANNED: 'Banned', DISABLED: 'Disabled' },
    directory: {
      searchPlaceholder: 'Search students',
      searchPlaceholderContact: 'Name, phone or email',
      searchPlaceholderName: 'Search by name',
      allCourses: 'All courses',
      courseLabel: 'Course',
      selectedCourse: 'Selected course',
      clearCourse: 'Remove the course filter',
      filterByCourse: 'Show only this course',
      statusLabel: 'Enrollment status',
      status: {
        any: 'Any status',
        active: 'Active',
        completed: 'Completed',
        inactive: 'No access',
        none: 'Not enrolled',
      },
      sortLabel: 'Sort by',
      sort: {
        lastSeen: 'Last seen',
        progress: 'Progress',
        name: 'Name',
        joined: 'Joined',
        enrolled: 'Enrolled',
      },
      atRiskFilter: 'At risk (7+ days)',
      toggleOrder: 'Reverse the sort order',
      orderAsc: 'Ascending',
      orderDesc: 'Descending',
      activityLabel: 'Activity',
      activity: {
        all: 'Any activity',
        online: 'Online now',
        today: 'Today',
        '7d': 'Last 7 days',
        inactive3: 'Inactive 3+ days',
        inactive7: 'Inactive 7+ days',
        inactive14: 'Inactive 14+ days',
        inactive30: 'Inactive 30+ days',
        never: 'Never seen',
      },
      clearFilters: 'Clear filters',
      total: 'Students: {count}',
      updated: 'Updated {time}',
      refresh: 'Refresh',
      loading: 'Loading students...',
      noMatchTitle: 'No students match these filters',
      noMatchDesc: 'Try another search or clear the filters.',
      emptyTitle: 'No students yet',
      emptyDesc: 'The students you can open will appear here.',
      cols: {
        student: 'Student',
        contact: 'Contact',
        progress: 'Progress',
        lesson: 'Current lesson',
        lastSeen: 'Last seen',
        streak: 'Streak',
      },
    },
    errors: {
      listTitle: 'Could not load the students',
      list: 'The list of students could not be loaded.',
      detail: 'This student could not be loaded.',
      title: 'Something went wrong',
      desc: 'Please try again.',
      retry: 'Retry',
      noAccessDesc: 'Your account does not have access to this page.',
    },
    detail: {
      loading: 'Loading student...',
      notFoundTitle: 'Student not found',
      notFoundDesc: 'This student does not exist, or is outside what your account can open.',
      toDirectory: 'Back to the directory',
      noAccessTitle: 'No access',
      title: 'Student',
      back: 'Back to students',
      joined: 'Joined',
      language: 'App language',
      locale: { uz: "O'zbek", ru: 'Русский', en: 'English' },
      courses: 'Courses',
      oneCourse: 'Showing one course.',
      showAllCourses: 'Show all courses',
      noCourses: 'Not enrolled in any course yet.',
      noCourseMatch: 'No enrollment in this course, or the course is outside what your account can open.',
      sections: 'Sections',
      allCourses: 'All courses',
      kpi: {
        progress: 'Avg. progress',
        courses: '{count} courses',
        streakDays: '{count} days',
        streakHint: 'Level {level} · {xp} XP',
        openTickets: 'Open tickets',
        ticketsHint: '{total} in all',
      },
      actions: { call: 'Call', email: 'Send email' },
    },
    course: {
      enrolled: 'Enrolled',
      completed: 'Completed',
      lastActivity: 'Last lesson activity',
      noActivity: 'No lesson started yet',
      lessonsDone: '{done} of {total} lessons done',
      showLessons: 'Show lessons',
      hideLessons: 'Hide lessons',
      noModule: 'Without a module',
      lessonStatus: {
        NOT_STARTED: 'Not started',
        IN_PROGRESS: 'In progress',
        COMPLETED: 'Completed',
      },
    },
    results: {
      title: 'Results',
      quizzes: 'Quizzes',
      exams: 'Exams',
      certificates: 'Certificates',
      noQuizzes: 'No quiz attempts yet.',
      noExams: 'No exam attempts yet.',
      leftExam: 'Left the exam: {count}',
      endedForLeaving: 'Ended for leaving the exam: {count}',
      noCertificates: 'No certificates yet.',
      passed: 'Passed',
      failed: 'Not passed',
      attempts: 'Attempts: {count}',
      distinction: 'With distinction',
      examStatus: {
        IN_PROGRESS: 'In progress',
        SUBMITTED: 'Submitted',
        GRADED: 'Graded',
        EXPIRED: 'Expired',
      },
      certificateStatus: { ISSUED: 'Issued', REVOKED: 'Revoked' },
    },
    practice: {
      title: 'Practice',
      level: 'Level',
      xpTotal: '{xp} XP in total',
      xpToNext: '{xp} XP to level {level}',
      streak: 'Streak',
      streakBest: 'best {days}',
      streakState: {
        active: 'Active today',
        pending: 'Needs practice today',
        frozen: 'Frozen',
        broken: 'Broken',
        none: 'No streak',
      },
      wordsKnown: 'Words known',
      listeningDone: 'Listening exercises: {count}',
      games: 'Games played',
      perfectRounds: 'Perfect rounds: {count}',
      last28: 'Last 28 days',
      activeDays: 'Active days: {count}',
      dayXp: '{xp} XP',
      legendGoal: 'Daily goal met ({xp} XP)',
      legendSome: 'Some practice',
      legendFrozen: 'Streak freeze',
      assessment: 'AI assessment',
      assessmentDetails: 'Details',
      assessmentScore: 'Risk {score} of 10',
      ledger: 'XP history (30 days)',
      noLedger: 'No practice in the last 30 days.',
      source: {
        GAME: 'Game',
        QUIZ: 'Quiz',
        EXAM: 'Exam',
        LISTENING: 'Listening',
        VOCABULARY: 'Vocabulary',
        LESSON: 'Lesson',
        ASSIGNMENT: 'Assignment',
        CERTIFICATE: 'Certificate',
        BADGE: 'Badge',
      },
      showMore: 'Show {count} more',
      showLess: 'Show less',
    },
    riskBand: { LOW: 'Low risk', MEDIUM: 'Medium risk', HIGH: 'High risk' },
    orders: {
      title: 'Orders',
      none: 'No orders yet.',
      provider: { PAYCOM: 'Payme', CLICK: 'Click' },
      status: {
        PENDING: 'Pending',
        PROCESSING: 'Processing',
        COMPLETED: 'Paid',
        FAILED: 'Failed',
        REFUNDED: 'Refunded',
        CANCELLED: 'Cancelled',
      },
    },
    tickets: {
      title: 'Support tickets',
      short: 'Tickets',
      open: 'Open: {count}',
      none: 'No tickets.',
      category: {
        COURSES: 'Courses',
        PAYMENT: 'Payment',
        TECHNICAL: 'Technical',
        ACCOUNT: 'Account',
        OTHER: 'Other',
      },
      status: {
        OPEN: 'Open',
        IN_PROGRESS: 'In progress',
        WAITING_USER: 'Waiting for the student',
        RESOLVED: 'Resolved',
        CLOSED: 'Closed',
      },
      priority: { LOW: 'Low', NORMAL: 'Normal', HIGH: 'High', URGENT: 'Urgent' },
    },
    risk: {
      emptyAdminTitle: 'Students at risk will appear here',
      emptyAdminDesc: 'This tab lists the AI risk score of the students assigned to you. To find students who have gone quiet, use the filters in the Directory.',
      openDirectory: 'Open the Directory',
      explain: 'The score runs from 1 to 10; 7 and above is high risk.',
      activityAny: 'Any activity',
      activity7: 'Active in the last 7 days',
      activity30: 'Active in the last 30 days',
      activity90: 'Active in the last 90 days',
      factor: {
        low_engagement: 'Low engagement',
        missed_deadlines: 'Missed deadlines',
        low_quiz_scores: 'Low quiz scores',
        low_exam_scores: 'Low exam scores',
        inactivity: 'Inactive for a long time',
        no_recent_activity: 'No recent activity',
        slow_progress: 'Slow progress',
        low_completion: 'Few lessons completed',
        failed_assignments: 'Assignments not completed',
        declining_performance: 'Results are declining',
        irregular_study: 'Irregular study',
        other: 'Other factor',
      },
    },
  },

  ru: {
    tabDirectory: 'Справочник',
    tabByCourse: 'По курсам',
    tabAiRisk: 'ИИ-оценка риска',
    atRisk: 'В зоне риска',
    atRiskHint: 'Записан на курс, активности нет 7 дней и более',
    hub: {
      title: 'Студенты',
      subtitle: 'Отслеживайте прогресс обучения и находите учеников, которым нужна помощь.',
      tabsLabel: 'Разделы студентов',
    },
    filters: { title: 'Фильтры', done: 'Готово' },
    roster: {
      selectCourse: 'Выберите курс...',
      searchPlaceholder: 'Поиск по имени, почте или телефону',
      subtitle: 'Список записей и прогресс по курсу',
      rosterCount: 'Записано студентов: {count}',
      pickTitle: 'Выберите курс, чтобы начать',
      pickDesc: 'Выберите один из своих курсов выше, чтобы увидеть записавшихся студентов, их прогресс и статус.',
      loading: 'Загрузка студентов...',
      emptyTitle: 'Пока нет записавшихся студентов',
      emptyDesc: 'Как только студенты запишутся на этот курс, они появятся здесь.',
      avgLine: 'Средний прогресс {percent}%',
      searchDisabled: 'Сначала выберите курс',
      filterAll: 'Все',
      filterInProgress: 'В процессе',
      filterCompleted: 'Завершено',
      filterNotStarted: 'Не начато',
      filterInactive: 'Нет доступа',
      statusCompleted: 'Завершено',
      statusInProgress: 'В процессе',
      statusNotStarted: 'Не начато',
      statusInactive: 'Нет доступа',
      colStudent: 'Студент',
      colProgress: 'Прогресс',
      colStatus: 'Статус',
      colGroups: 'Группы',
      colEnrolled: 'Записан',
      colCompleted: 'Завершил',
      noMatch: 'Нет студентов по этому фильтру.',
      showing: 'Показано {shown} из {total} студентов',
      showingAll: 'Показано студентов: {shown}',
      rosterCapped: 'Загружены только первые {loaded} из {total} студентов; счётчики и поиск учитывают только их.',
      allGroups: 'Все группы',
      addToGroup: 'Добавить в группу',
      assignHead: 'Добавить в группу',
      inAllGroups: 'Уже во всех группах',
      noGroupsYet: 'Групп пока нет. Создайте их на странице «Команды».',
      addedToGroup: 'Добавлен в {group}',
      addGroupFailed: 'Не удалось добавить в группу',
      viewDetail: 'Детали студента',
    },
    notEnrolled: 'Нет записи на курсы',
    moreCourses: '+{count} ещё',
    joinedOn: 'Регистрация: {date}',
    ago: {
      justNow: 'только что',
      minutes: '{n} мин назад',
      hours: '{n} ч назад',
      days: '{n} дн. назад',
      today: 'сегодня',
      yesterday: 'вчера',
    },
    presence: {
      online: 'Сейчас онлайн',
      onlineHint: 'Была активность за последние 5 минут',
      never: 'Нет активности',
      neverHint: 'Заходов пока не зафиксировано',
      approximateHint: 'Приблизительно: по последней учебной активности. Точный учёт начнётся со следующего визита.',
      tashkentTime: 'время Ташкента',
      loginUnknownHint: 'Пока не зафиксирован: появится при следующем входе',
      lastSeen: 'Последняя активность',
      lastLogin: 'Последний вход',
      loginAgo: 'Вход: {when}',
    },
    lesson: {
      current: 'Текущий урок',
      short: '{index} / {total}',
      position: 'Урок {index} из {total}',
      pausedAt: 'остановлено на {time}',
      reason: {
        inProgress: 'В процессе',
        next: 'Следующий',
        first: 'Не начато',
        finished: 'Курс пройден',
      },
    },
    enrollment: { ACTIVE: 'Активно', COMPLETED: 'Завершено', INACTIVE: 'Нет доступа' },
    account: { BANNED: 'Заблокирован', DISABLED: 'Отключён' },
    directory: {
      searchPlaceholder: 'Поиск студентов',
      searchPlaceholderContact: 'Имя, телефон или почта',
      searchPlaceholderName: 'Поиск по имени',
      allCourses: 'Все курсы',
      courseLabel: 'Курс',
      selectedCourse: 'Выбранный курс',
      clearCourse: 'Убрать фильтр по курсу',
      filterByCourse: 'Показать только этот курс',
      statusLabel: 'Статус записи',
      status: {
        any: 'Любой статус',
        active: 'Активные',
        completed: 'Завершили',
        inactive: 'Нет доступа',
        none: 'Нет записи на курсы',
      },
      sortLabel: 'Сортировка',
      sort: {
        lastSeen: 'Последняя активность',
        progress: 'Прогресс',
        name: 'Имя',
        joined: 'Дата регистрации',
        enrolled: 'Дата записи',
      },
      atRiskFilter: 'В зоне риска (7+ дн.)',
      toggleOrder: 'Изменить порядок сортировки',
      orderAsc: 'По возрастанию',
      orderDesc: 'По убыванию',
      activityLabel: 'Активность',
      activity: {
        all: 'Любая активность',
        online: 'Онлайн',
        today: 'Сегодня',
        '7d': 'За 7 дней',
        inactive3: 'Неактивны 3+ дн.',
        inactive7: 'Неактивны 7+ дн.',
        inactive14: 'Неактивны 14+ дн.',
        inactive30: 'Неактивны 30+ дн.',
        never: 'Не заходили',
      },
      clearFilters: 'Сбросить фильтры',
      total: 'Студентов: {count}',
      updated: 'Обновлено в {time}',
      refresh: 'Обновить',
      loading: 'Загрузка студентов...',
      noMatchTitle: 'Нет студентов по этим фильтрам',
      noMatchDesc: 'Измените запрос или сбросьте фильтры.',
      emptyTitle: 'Пока нет студентов',
      emptyDesc: 'Здесь появятся студенты, к которым у вас есть доступ.',
      cols: {
        student: 'Студент',
        contact: 'Контакты',
        progress: 'Прогресс',
        lesson: 'Текущий урок',
        lastSeen: 'Последняя активность',
        streak: 'Серия',
      },
    },
    errors: {
      listTitle: 'Не удалось загрузить студентов',
      list: 'Не удалось загрузить список студентов.',
      detail: 'Не удалось загрузить данные студента.',
      title: 'Произошла ошибка',
      desc: 'Пожалуйста, попробуйте ещё раз.',
      retry: 'Повторить',
      noAccessDesc: 'У вашей учётной записи нет доступа к этой странице.',
    },
    detail: {
      loading: 'Загрузка студента...',
      notFoundTitle: 'Студент не найден',
      notFoundDesc: 'Такого студента нет, или он недоступен для вашей учётной записи.',
      toDirectory: 'К справочнику',
      noAccessTitle: 'Нет доступа',
      title: 'Студент',
      back: 'К списку студентов',
      joined: 'Регистрация',
      language: 'Язык приложения',
      locale: { uz: "O'zbek", ru: 'Русский', en: 'English' },
      courses: 'Курсы',
      oneCourse: 'Показан один курс.',
      showAllCourses: 'Показать все курсы',
      noCourses: 'Студент пока не записан ни на один курс.',
      noCourseMatch: 'Записи на этот курс нет, или курс недоступен для вашей учётной записи.',
      sections: 'Разделы',
      allCourses: 'Все курсы',
      kpi: {
        progress: 'Средний прогресс',
        courses: 'Курсов: {count}',
        streakDays: '{count} дн.',
        streakHint: 'Уровень {level} · {xp} XP',
        openTickets: 'Открытые обращения',
        ticketsHint: 'Всего: {total}',
      },
      actions: { call: 'Позвонить', email: 'Написать на почту' },
    },
    course: {
      enrolled: 'Запись',
      completed: 'Завершение',
      lastActivity: 'Активность в уроках',
      noActivity: 'Уроки ещё не начинались',
      lessonsDone: 'Пройдено уроков: {done} из {total}',
      showLessons: 'Показать уроки',
      hideLessons: 'Скрыть уроки',
      noModule: 'Без модуля',
      lessonStatus: {
        NOT_STARTED: 'Не начат',
        IN_PROGRESS: 'В процессе',
        COMPLETED: 'Пройден',
      },
    },
    results: {
      title: 'Результаты',
      quizzes: 'Тесты',
      exams: 'Экзамены',
      certificates: 'Сертификаты',
      noQuizzes: 'Попыток тестов пока нет.',
      noExams: 'Попыток экзаменов пока нет.',
      leftExam: 'Покидал экзамен: {count}',
      endedForLeaving: 'Завершён за выходы из экзамена: {count}',
      noCertificates: 'Сертификатов пока нет.',
      passed: 'Сдан',
      failed: 'Не сдан',
      attempts: 'Попыток: {count}',
      distinction: 'С отличием',
      examStatus: {
        IN_PROGRESS: 'Идёт',
        SUBMITTED: 'Отправлен',
        GRADED: 'Проверен',
        EXPIRED: 'Время вышло',
      },
      certificateStatus: { ISSUED: 'Выдан', REVOKED: 'Отозван' },
    },
    practice: {
      title: 'Практика',
      level: 'Уровень',
      xpTotal: 'Всего {xp} XP',
      xpToNext: '{xp} XP до уровня {level}',
      streak: 'Серия',
      streakBest: 'рекорд {days}',
      streakState: {
        active: 'Активна сегодня',
        pending: 'Нужна практика сегодня',
        frozen: 'Заморожена',
        broken: 'Прервана',
        none: 'Нет серии',
      },
      wordsKnown: 'Выученных слов',
      listeningDone: 'Упражнений на аудирование: {count}',
      games: 'Сыграно игр',
      perfectRounds: 'Идеальных раундов: {count}',
      last28: 'Последние 28 дней',
      activeDays: 'Активных дней: {count}',
      dayXp: '{xp} XP',
      legendGoal: 'Дневная цель выполнена ({xp} XP)',
      legendSome: 'Была практика',
      legendFrozen: 'Заморозка серии',
      assessment: 'Оценка ИИ',
      assessmentDetails: 'Подробнее',
      assessmentScore: 'Риск {score} из 10',
      ledger: 'История XP (30 дней)',
      noLedger: 'За последние 30 дней практики не было.',
      source: {
        GAME: 'Игра',
        QUIZ: 'Тест',
        EXAM: 'Экзамен',
        LISTENING: 'Аудирование',
        VOCABULARY: 'Слова',
        LESSON: 'Урок',
        ASSIGNMENT: 'Задание',
        CERTIFICATE: 'Сертификат',
        BADGE: 'Значок',
      },
      showMore: 'Показать ещё {count}',
      showLess: 'Свернуть',
    },
    riskBand: { LOW: 'Низкий риск', MEDIUM: 'Средний риск', HIGH: 'Высокий риск' },
    orders: {
      title: 'Заказы',
      none: 'Заказов пока нет.',
      provider: { PAYCOM: 'Payme', CLICK: 'Click' },
      status: {
        PENDING: 'Ожидает',
        PROCESSING: 'Обрабатывается',
        COMPLETED: 'Оплачен',
        FAILED: 'Ошибка',
        REFUNDED: 'Возврат',
        CANCELLED: 'Отменён',
      },
    },
    tickets: {
      title: 'Обращения в поддержку',
      short: 'Обращения',
      open: 'Открыто: {count}',
      none: 'Обращений нет.',
      category: {
        COURSES: 'Курсы',
        PAYMENT: 'Оплата',
        TECHNICAL: 'Технические',
        ACCOUNT: 'Аккаунт',
        OTHER: 'Другое',
      },
      status: {
        OPEN: 'Открыто',
        IN_PROGRESS: 'В работе',
        WAITING_USER: 'Ждём студента',
        RESOLVED: 'Решено',
        CLOSED: 'Закрыто',
      },
      priority: { LOW: 'Низкий', NORMAL: 'Обычный', HIGH: 'Высокий', URGENT: 'Срочный' },
    },
    risk: {
      emptyAdminTitle: 'Здесь появятся ученики в зоне риска',
      emptyAdminDesc: 'На этой вкладке показана ИИ-оценка риска учеников, закреплённых за вами. Чтобы найти тех, кто пропал, используйте фильтры в списке.',
      openDirectory: 'Открыть список',
      explain: 'Оценка идёт от 1 до 10; 7 и выше — высокий риск.',
      activityAny: 'Любая активность',
      activity7: 'Активны за последние 7 дней',
      activity30: 'Активны за последние 30 дней',
      activity90: 'Активны за последние 90 дней',
      factor: {
        low_engagement: 'Низкая вовлечённость',
        missed_deadlines: 'Пропущенные сроки',
        low_quiz_scores: 'Низкие баллы за тесты',
        low_exam_scores: 'Низкие баллы за экзамены',
        inactivity: 'Долго не активен',
        no_recent_activity: 'Нет недавней активности',
        slow_progress: 'Медленный прогресс',
        low_completion: 'Мало пройденных уроков',
        failed_assignments: 'Задания не выполнены',
        declining_performance: 'Результаты снижаются',
        irregular_study: 'Нерегулярные занятия',
        other: 'Другой фактор',
      },
    },
  },

  uz: {
    tabDirectory: "Ro'yxat",
    tabByCourse: "Kurslar bo'yicha",
    tabAiRisk: 'AI xavf bahosi',
    atRisk: 'Xavf ostida',
    atRiskHint: "Kursga yozilgan va 7 kun yoki undan ko'p vaqt faol bo'lmagan",
    hub: {
      title: "O'quvchilar",
      subtitle: "O'quvchilarning progressini kuzating va e'tibor talab qiladiganlarni toping.",
      tabsLabel: "O'quvchilar bo'limlari",
    },
    filters: { title: 'Filtrlar', done: 'Tayyor' },
    roster: {
      selectCourse: 'Kursni tanlang...',
      searchPlaceholder: "Ism, email yoki telefon bo'yicha qidirish",
      subtitle: "Kurs bo'yicha ro'yxat va progress",
      rosterCount: "{count} ta o'quvchi yozilgan",
      pickTitle: 'Boshlash uchun kursni tanlang',
      pickDesc: "Kurslaringizdan birini tanlang: o'quvchilar, ularning progressi va holatini ko'rasiz.",
      loading: "O'quvchilar yuklanmoqda...",
      emptyTitle: "Hali o'quvchilar yozilmagan",
      emptyDesc: "O'quvchilar bu kursga yozilgach, shu yerda paydo bo'ladi.",
      avgLine: "O'rtacha progress: {percent}%",
      searchDisabled: 'Avval kursni tanlang',
      filterAll: 'Barchasi',
      filterInProgress: 'Jarayonda',
      filterCompleted: 'Tugatgan',
      filterNotStarted: 'Boshlanmagan',
      filterInactive: 'Kirish yopilgan',
      statusCompleted: 'Tugatgan',
      statusInProgress: 'Jarayonda',
      statusNotStarted: 'Boshlanmagan',
      statusInactive: 'Kirish yopilgan',
      colStudent: "O'quvchi",
      colProgress: 'Progress',
      colStatus: 'Holat',
      colGroups: 'Guruhlar',
      colEnrolled: 'Yozilgan',
      colCompleted: 'Tugatgan',
      noMatch: "Bu filtrga mos o'quvchi yo'q.",
      showing: "{total} tadan {shown} ta o'quvchi ko'rsatilmoqda",
      showingAll: "{shown} ta o'quvchi ko'rsatilmoqda",
      rosterCapped: "Faqat birinchi {loaded} ta o'quvchi ({total} tadan) yuklangan; hisoblagichlar va qidiruv faqat shularni qamrab oladi.",
      allGroups: 'Barcha guruhlar',
      addToGroup: "Guruhga qo'shish",
      assignHead: "Guruhga qo'shish",
      inAllGroups: 'Barcha guruhlarda bor',
      noGroupsYet: "Hali guruhlar yo'q. Ularni Jamoalar sahifasida yarating.",
      addedToGroup: "{group} guruhiga qo'shildi",
      addGroupFailed: "Guruhga qo'shib bo'lmadi",
      viewDetail: "O'quvchi tafsilotlari",
    },
    notEnrolled: 'Kursga yozilmagan',
    moreCourses: '+{count} ta yana',
    joinedOn: "Ro'yxatdan o'tgan: {date}",
    ago: {
      justNow: 'hozirgina',
      minutes: '{n} daqiqa oldin',
      hours: '{n} soat oldin',
      days: '{n} kun oldin',
      today: 'bugun',
      yesterday: 'kecha',
    },
    presence: {
      online: 'Hozir onlayn',
      onlineHint: "Oxirgi 5 daqiqada faol bo'lgan",
      never: 'Hali kirmagan',
      neverHint: 'Hozircha kirish qayd etilmagan',
      approximateHint: "Taxminiy: oxirgi o'quv faolligi bo'yicha. Aniq hisob keyingi kirishdan boshlanadi.",
      tashkentTime: 'Toshkent vaqti',
      loginUnknownHint: "Hozircha qayd etilmagan: keyingi kirishda paydo bo'ladi",
      lastSeen: 'Oxirgi faollik',
      lastLogin: 'Oxirgi kirish',
      loginAgo: 'Kirish: {when}',
    },
    lesson: {
      current: 'Joriy dars',
      short: '{index} / {total}',
      position: '{total} tadan {index}-dars',
      pausedAt: "{time} da to'xtagan",
      reason: {
        inProgress: 'Jarayonda',
        next: 'Navbatdagi',
        first: 'Boshlanmagan',
        finished: 'Kurs tugagan',
      },
    },
    enrollment: { ACTIVE: 'Faol', COMPLETED: 'Tugatilgan', INACTIVE: 'Kirish yopilgan' },
    account: { BANNED: 'Bloklangan', DISABLED: "O'chirilgan" },
    directory: {
      searchPlaceholder: "O'quvchilarni qidirish",
      searchPlaceholderContact: 'Ism, telefon yoki email',
      searchPlaceholderName: "Ism bo'yicha qidirish",
      allCourses: 'Barcha kurslar',
      courseLabel: 'Kurs',
      selectedCourse: 'Tanlangan kurs',
      clearCourse: 'Kurs filtrini olib tashlash',
      filterByCourse: "Faqat shu kursni ko'rsatish",
      statusLabel: 'Yozilish holati',
      status: {
        any: 'Istalgan holat',
        active: 'Faol',
        completed: 'Tugatgan',
        inactive: 'Kirish yopilgan',
        none: 'Kursga yozilmagan',
      },
      sortLabel: 'Saralash',
      sort: {
        lastSeen: 'Oxirgi faollik',
        progress: 'Progress',
        name: 'Ism',
        joined: "Ro'yxatdan o'tgan sana",
        enrolled: 'Yozilgan sana',
      },
      atRiskFilter: 'Xavf ostida (7+ kun)',
      toggleOrder: 'Saralash tartibini almashtirish',
      orderAsc: "O'sish bo'yicha",
      orderDesc: "Kamayish bo'yicha",
      activityLabel: 'Faollik',
      activity: {
        all: 'Istalgan faollik',
        online: 'Onlayn',
        today: 'Bugun',
        '7d': 'Oxirgi 7 kun',
        inactive3: '3+ kun faol emas',
        inactive7: '7+ kun faol emas',
        inactive14: '14+ kun faol emas',
        inactive30: '30+ kun faol emas',
        never: 'Hali kirmagan',
      },
      clearFilters: 'Filtrlarni tozalash',
      total: "O'quvchilar: {count}",
      updated: 'Yangilandi: {time}',
      refresh: 'Yangilash',
      loading: "O'quvchilar yuklanmoqda...",
      noMatchTitle: "Bu filtrlarga mos o'quvchi yo'q",
      noMatchDesc: "Boshqa so'rov kiriting yoki filtrlarni tozalang.",
      emptyTitle: "Hozircha o'quvchilar yo'q",
      emptyDesc: "Siz ko'ra oladigan o'quvchilar shu yerda paydo bo'ladi.",
      cols: {
        student: "O'quvchi",
        contact: 'Aloqa',
        progress: 'Progress',
        lesson: 'Joriy dars',
        lastSeen: 'Oxirgi faollik',
        streak: 'Seriya',
      },
    },
    errors: {
      listTitle: "O'quvchilarni yuklab bo'lmadi",
      list: "O'quvchilar ro'yxatini yuklab bo'lmadi.",
      detail: "O'quvchi ma'lumotlarini yuklab bo'lmadi.",
      title: 'Xatolik yuz berdi',
      desc: "Iltimos, qaytadan urinib ko'ring.",
      retry: 'Qaytadan',
      noAccessDesc: "Hisobingizda bu sahifani ochish huquqi yo'q.",
    },
    detail: {
      loading: "O'quvchi yuklanmoqda...",
      notFoundTitle: "O'quvchi topilmadi",
      notFoundDesc: "Bunday o'quvchi yo'q yoki sizning hisobingiz uchun ochiq emas.",
      toDirectory: "Ro'yxatga qaytish",
      noAccessTitle: "Ruxsat yo'q",
      title: "O'quvchi",
      back: "O'quvchilar ro'yxatiga",
      joined: "Ro'yxatdan o'tgan",
      language: 'Ilova tili',
      locale: { uz: "O'zbek", ru: 'Русский', en: 'English' },
      courses: 'Kurslar',
      oneCourse: "Bitta kurs ko'rsatilgan.",
      showAllCourses: "Barcha kurslarni ko'rsatish",
      noCourses: "O'quvchi hali birorta kursga yozilmagan.",
      noCourseMatch: "Bu kursga yozilish yo'q yoki kurs hisobingiz uchun ochiq emas.",
      sections: "Bo'limlar",
      allCourses: 'Barcha kurslar',
      kpi: {
        progress: "O'rtacha progress",
        courses: '{count} ta kurs',
        streakDays: '{count} kun',
        streakHint: 'Daraja {level} · {xp} XP',
        openTickets: 'Ochiq murojaatlar',
        ticketsHint: 'jami {total} ta',
      },
      actions: { call: "Qo'ng'iroq qilish", email: 'Email yuborish' },
    },
    course: {
      enrolled: 'Yozilgan',
      completed: 'Tugatilgan',
      lastActivity: 'Darslardagi faollik',
      noActivity: 'Darslar hali boshlanmagan',
      lessonsDone: '{total} tadan {done} ta dars tugatilgan',
      showLessons: "Darslarni ko'rsatish",
      hideLessons: 'Darslarni yashirish',
      noModule: 'Modulsiz',
      lessonStatus: {
        NOT_STARTED: 'Boshlanmagan',
        IN_PROGRESS: 'Jarayonda',
        COMPLETED: 'Tugatilgan',
      },
    },
    results: {
      title: 'Natijalar',
      quizzes: 'Testlar',
      exams: 'Imtihonlar',
      certificates: 'Sertifikatlar',
      noQuizzes: "Test urinishlari hali yo'q.",
      noExams: "Imtihon urinishlari hali yo'q.",
      leftExam: "Imtihondan chiqib ketgan: {count}",
      endedForLeaving: "Chiqib ketgani uchun yakunlangan: {count}",
      noCertificates: "Sertifikatlar hali yo'q.",
      passed: "O'tgan",
      failed: "O'tmagan",
      attempts: 'Urinishlar: {count}',
      distinction: 'Imtiyozli',
      examStatus: {
        IN_PROGRESS: 'Jarayonda',
        SUBMITTED: 'Topshirilgan',
        GRADED: 'Baholangan',
        EXPIRED: 'Vaqt tugagan',
      },
      certificateStatus: { ISSUED: 'Berilgan', REVOKED: 'Bekor qilingan' },
    },
    practice: {
      title: 'Mashq',
      level: 'Daraja',
      xpTotal: 'Jami {xp} XP',
      xpToNext: '{level}-darajagacha {xp} XP',
      streak: 'Seriya',
      streakBest: 'rekord: {days}',
      streakState: {
        active: 'Bugun faol',
        pending: 'Bugun mashq kerak',
        frozen: 'Muzlatilgan',
        broken: 'Uzilgan',
        none: "Seriya yo'q",
      },
      wordsKnown: "Bilgan so'zlar",
      listeningDone: 'Tinglash mashqlari: {count}',
      games: "O'ynalgan o'yinlar",
      perfectRounds: 'Mukammal raundlar: {count}',
      last28: 'Oxirgi 28 kun',
      activeDays: 'Faol kunlar: {count}',
      dayXp: '{xp} XP',
      legendGoal: 'Kunlik maqsad bajarilgan ({xp} XP)',
      legendSome: "Mashq bo'lgan",
      legendFrozen: 'Seriyani muzlatish',
      assessment: 'AI bahosi',
      assessmentDetails: 'Batafsil',
      assessmentScore: 'Xavf: {score} / 10',
      ledger: 'XP tarixi (30 kun)',
      noLedger: "Oxirgi 30 kunda mashq bo'lmagan.",
      source: {
        GAME: "O'yin",
        QUIZ: 'Test',
        EXAM: 'Imtihon',
        LISTENING: 'Tinglash',
        VOCABULARY: "So'zlar",
        LESSON: 'Dars',
        ASSIGNMENT: 'Topshiriq',
        CERTIFICATE: 'Sertifikat',
        BADGE: 'Nishon',
      },
      showMore: "Yana {count} ta ko'rsatish",
      showLess: "Yig'ish",
    },
    riskBand: { LOW: 'Past xavf', MEDIUM: "O'rta xavf", HIGH: 'Yuqori xavf' },
    orders: {
      title: 'Buyurtmalar',
      none: "Buyurtmalar hali yo'q.",
      provider: { PAYCOM: 'Payme', CLICK: 'Click' },
      status: {
        PENDING: 'Kutilmoqda',
        PROCESSING: 'Ishlanmoqda',
        COMPLETED: "To'langan",
        FAILED: 'Xatolik',
        REFUNDED: 'Qaytarilgan',
        CANCELLED: 'Bekor qilingan',
      },
    },
    tickets: {
      title: "Qo'llab-quvvatlash murojaatlari",
      short: 'Murojaatlar',
      open: 'Ochiq: {count}',
      none: "Murojaatlar yo'q.",
      category: {
        COURSES: 'Kurslar',
        PAYMENT: "To'lov",
        TECHNICAL: 'Texnik',
        ACCOUNT: 'Hisob',
        OTHER: 'Boshqa',
      },
      status: {
        OPEN: 'Ochiq',
        IN_PROGRESS: 'Ishda',
        WAITING_USER: "O'quvchi javobi kutilmoqda",
        RESOLVED: 'Hal qilingan',
        CLOSED: 'Yopilgan',
      },
      priority: { LOW: 'Past', NORMAL: 'Oddiy', HIGH: 'Yuqori', URGENT: 'Shoshilinch' },
    },
    risk: {
      emptyAdminTitle: "Xavf ostidagi o'quvchilar shu yerda ko'rinadi",
      emptyAdminDesc: "Bu bo'lim sizga biriktirilgan o'quvchilarning AI xavf bahosini ko'rsatadi. Faolligi pasayganlarni topish uchun Ro'yxatdagi filtrlardan foydalaning.",
      openDirectory: "Ro'yxatni ochish",
      explain: "Baho 1 dan 10 gacha; 7 va undan yuqori — yuqori xavf.",
      activityAny: 'Istalgan faollik',
      activity7: "Oxirgi 7 kunda faol",
      activity30: "Oxirgi 30 kunda faol",
      activity90: "Oxirgi 90 kunda faol",
      factor: {
        low_engagement: 'Faollik past',
        missed_deadlines: "Muddatlar o'tkazib yuborilgan",
        low_quiz_scores: 'Test natijalari past',
        low_exam_scores: 'Imtihon natijalari past',
        inactivity: 'Uzoq vaqt faol emas',
        no_recent_activity: "Yaqinda faollik yo'q",
        slow_progress: 'Progress sust',
        low_completion: 'Kam dars tugatilgan',
        failed_assignments: 'Topshiriqlar bajarilmagan',
        declining_performance: 'Natijalar pasaymoqda',
        irregular_study: "Mashg'ulotlar tartibsiz",
        other: 'Boshqa omil',
      },
    },
  },
};

export default student360;
